import { DateTime } from 'luxon';
import { CSSProperties, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const KibiUnit = 1024; // for Kibibyte
export const KiloUnit = 1000; // for Kilobyte
export const MY_ACCOUNT_LIST = 'my-account-list';

export const UPGRADE_STATE = {
  NEED_UPGRADE: 'NEED_UPGRADE',
  UPGRADE_SCHEDULED: 'UPGRADE_SCHEDULED',
  UPGRADING: 'UPGRADING',
  UPGRADE_SUCCESS: 'UPGRADE_SUCCESS',
  UPGRADE_ERROR: 'UPGRADE_ERROR',
  OTHER_TASK_PENDING: 'OTHER_TASK_PENDING'
};

export const SNAPSHOT_STATE = {
  REVERT_START: 'START',
  REVERT_SUCCESS: 'SUCCESS',
  SNAPSHOT_START: 'START',
  SNAPSHOT_SUCCESS: 'SUCCESS',
  SNAPSHOT_REBOOT_DONE: 'SNAPSHOT_REBOOT_DONE',
  SNAPSHOT_ERROR: 'ERROR'
};

export const copyObj = (obj: Object) => JSON.parse(JSON.stringify(obj));

export const getCoords = (elem: any) => {
  // crossbrowser version
  const box = elem.getBoundingClientRect();

  const body = document.body;
  const docEl = document.documentElement;

  const scrollTop = window.scrollY || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.scrollX || docEl.scrollLeft || body.scrollLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;

  const top = box.top + scrollTop - clientTop;
  const left = box.left + scrollLeft - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };
};

export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/**
 * usage :
  const isFirstRender = useIsFirst();
  useEffect(() => {
    if(isFirstRender){
      ...
    }
  }, [count])
 *
 */
export const useIsFirst = () => {
  const isFirstRef = useRef(true);
  useEffect(() => {
    isFirstRef.current = false;
  }, []);
  return isFirstRef.current;
};

export const getSafeUrl = (url: string) => {
  const dest = new URL(url);
  return dest.href;
};
export const getSafeUrlWithoutToken = (url: string) => {
  const u = new URL(url);
  u.searchParams.delete('token');
  u.searchParams.delete('cloud_token');
  return u.toString();
};
export const getMessageStyle = (type: string) => {
  const styles: Record<string, CSSProperties> = {
    info: {
      color: '#01598f',
      backgroundColor: '#c2eefc'
    },
    error: {
      color: '#721c24',
      backgroundColor: '#f8d7da'
    },
    success: {
      color: '#155724',
      backgroundColor: '#d4edda'
    },
    warning: {
      color: '#856404',
      backgroundColor: '#fff3cd'
    }
  };
  return type && styles[type] ? styles[type] : styles.info;
};

export const extractToken = (url: string) => {
  const u = new URL(url);
  return u.searchParams.get('token') || u.searchParams.get('cloud_token') || '';
};

export const formatMbToGb = (num: number | string): string => {
  return num ? (Number(num) / KiloUnit).toFixed(1) : '0';
};

export const quotaHistoryMbToGb = (num: number | string): string => {
  return num ? (Number(num) / KibiUnit).toFixed(2) : '0';
};

export const formatPercent = (percent: number | string): number => {
  return percent ? Number(Number(percent).toFixed(1)) : 0;
};

export const getScheduledTime = (timeStr: string) => {
  const date = new Date(timeStr);
  let year = date.getFullYear();
  let month =
    date.getMonth() + 1 < 10
      ? '0' + (date.getMonth() + 1)
      : date.getMonth() + 1;
  let day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
  let hours = date.getHours() < 10 ? '0' + date.getHours() : date.getHours();
  let mins =
    date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();

  return { date: year + '-' + month + '-' + day, time: hours + ':' + mins };
};

export const validLocaleList = [
  'enUS',
  'zhCN',
  'zhTW',
  // 'frFR',
  'jaJP',
  // 'esES',
  'koKR',
  'auto'
];
export const i18nMather = (navigatorLanguage: string) => {
  const strArr: string[] = navigatorLanguage.split('-');
  const matchedValidLocale = (keys: string[]): string => {
    const keyString = keys.join('');
    if (validLocaleList.includes(keyString)) {
      return keyString;
    } else if (keys.length > 1) {
      const newKeys: string[] = keys.slice(-1);
      return matchedValidLocale(newKeys);
    }
    const regex = new RegExp(`^${keyString}.*$`);
    const preMatched = validLocaleList.find((locale: string) =>
      regex.test(locale)
    );
    return preMatched || validLocaleList[0];
  };
  return matchedValidLocale(strArr);
};

export const localeToNavigatorLanguage = (locale: string): string => {
  let str = locale || '';
  if (str === 'auto') {
    str = getNavigatorLanguage();
  }
  const arr = str.match(/.{1,2}/g);
  if (arr?.length) {
    return arr.join('-');
  }
  return locale;
};

export const getNavigatorLanguage = (): string => {
  if (navigator.languages?.length) {
    const navigatorLanguage: string = navigator.languages[0];
    return i18nMather(navigatorLanguage);
  }
  return 'enUS';
};

export const getDomainFromUrl = (url: string): string => {
  const host = new URL(url);
  return host?.hostname || '';
};

// Daylight Saving Time (DST)
const stdTimezoneOffset = () => {
  const now = new Date();
  const year = now.getFullYear();
  const jan = new Date(year, 0, 1);
  const jul = new Date(year, 6, 1);
  const stdTimeOffset = Math.max(
    jan.getTimezoneOffset(),
    jul.getTimezoneOffset()
  );
  return stdTimeOffset;
};
export const getClientTimezone = () => {
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // const timeOffset = -(new Date().getTimezoneOffset() / 60);
  const timeOffset = -(stdTimezoneOffset() / 60);
  const timeOffsetStr = Math.ceil(timeOffset).toString();
  return {
    text: systemTimezone,
    value: timeOffsetStr.startsWith('-') ? timeOffsetStr : '+' + timeOffsetStr
  };
};

export const getDateDiffMinutes = (dt2: Date, dt1: Date) => {
  let diff = (dt2.getTime() - dt1.getTime()) / 1000;
  diff /= 60;
  return Math.abs(Math.round(diff));
};

export const getTimeObj = (date: any) => {
  let year = date.year;
  let month = date.month + 1 < 10 ? '0' + date.month : date.month;
  let day = date.day < 10 ? '0' + date.day : date.day;
  let hours = date.hour < 10 ? '0' + date.hour : date.hour;
  let mins = date.minute < 10 ? '0' + date.minute : date.minute;
  return { date: year + '-' + month + '-' + day, time: hours + ':' + mins };
};

export const getTimeFromTimestamp = (ts: number) => {
  const timeInfo = new Date(ts * 1000);
  const localeOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  };
  const localeTime = timeInfo
    .toLocaleString('en-CA', localeOptions)
    .replace(/,/, '');
  return localeTime;
};

export const parseRedirectAccountFromHkey = (str: string) => {
  try {
    const dataString = decodeURIComponent(str || '');
    const data = JSON.parse(dataString);
    if (data.account_id) {
      return data;
    }
    return {};
  } catch (e) {
    return {};
  }
};
interface IProps {
  account?: Record<string, any>;
  instance?: Record<string, any>;
  region: Record<string, any>;
  account_id?: string;
  instance_id?: string;
}

export const formatQueryRes = ({
  account,
  instance,
  region,
  account_id,
  instance_id
}: IProps) => {
  const groupId = account?.group_id;
  const accountId = account_id || account?.account_id;
  const instanceId = instance_id || instance?.instance_id;
  const regionUrl = region?.url;
  const regionToken = region?.token;
  const regionName = region?.alias;
  const access_from_user_id =
    account?.access_from_user_id || account?.access_from?.user_id;
  const access_from_account_id =
    account?.access_from_account_id || account?.access_from?.account_id;
  const user_id = account?.user_id;
  const account_node_id = account?.account_node_id;
  const org_id = account?.org_id;
  return {
    account: {
      account_id: accountId,
      group_id: groupId,
      access_from_user_id,
      access_from_account_id,
      user_id,
      org_id,
      account_node_id
    },
    instance: { instance_id: instanceId },
    region: { url: regionUrl, token: regionToken, alias: regionName }
  };
};

export const getDateStr = (inDate: string) => {
  if (inDate) {
    const date = DateTime.fromISO(inDate);
    const humanReadable = date.toLocaleString(DateTime.DATE_MED);
    return humanReadable;
  }
  return '';
};

export const getDateStrFromUnixTimestamp = (ts: number, formatter?: string) => {
  if (ts) {
    try {
      return DateTime.fromSeconds(ts)
        .setZone('local')
        .toFormat(formatter || 'yyyy-MM-dd HH:mm:ss');
    } catch (error) {
      console.warn(' format time error', JSON.stringify(error));
      return '';
    }
  }
  return '';
};

export const convertDaysToMonthsAndDays = (totalDays: number) => {
  const daysInMonth = 30; // You can adjust if you want 31 or use a calendar-based calc
  const months = Math.floor(totalDays / daysInMonth);
  const days = totalDays % daysInMonth;

  if (months > 0) {
    return { months, days };
  }

  return { days };
};

export const formatDays = (totalDays: number): string => {
  const daysInMonth = 30;
  const months = Math.floor(totalDays / daysInMonth);
  const days = totalDays % daysInMonth;

  if (months > 0) {
    return `${months} month${months > 1 ? 's' : ''}${
      days > 0 ? ` ${days} day${days > 1 ? 's' : ''}` : ''
    }`;
  }

  return `${days} day${days !== 1 ? 's' : ''}`;
};
export const daysToMonths = (days: number): number => {
  if (days <= 0) return 0;
  return Math.ceil(days / 30.44);
};

export const isBeforeTodayPlusDays = (
  dateStr: string,
  diffDays: number
): boolean => {
  const inputDate = new Date(dateStr);
  const diff = Number(diffDays || 0);
  if (isNaN(inputDate.getTime()) || isNaN(diff)) {
    // throw new Error("Invalid date string format. Expected 'YYYY-MM-DD'.");
    console.warn('Invalid date string or diff days');
    return true;
  }

  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + diff);

  // Compare only the date parts (ignore time)
  inputDate.setHours(0, 0, 0, 0);
  futureDate.setHours(0, 0, 0, 0);

  return inputDate < futureDate;
};
